import type { CarbonIconType } from "@carbon/icons-react";
import {
  Button as HeroButton,
  type ButtonProps as HeroButtonProps,
} from "@heroui/react";
import type { ReactNode } from "react";
import type { ButtonColor, ButtonVariant } from "./buttonConstants";
import { isLinkButton } from "./buttonUtils";
import LinkButton from "./LinkButton";
import { resolveButtonProps } from "./resolveButtonProps";

export interface ButtonBaseProps
  extends Omit<
    HeroButtonProps,
    "variant" | "color" | "children" | "className"
  > {
  /**
   * Test ID for DOM queries (e.g. in tests)
   */
  "data-testid"?: string;
  /**
   * Button variant style
   * - primary: Filled with brand blue color
   * - secondary: Bordered button (use color="danger" for red border)
   * - tertiary: Light button without border or fill
   */
  variant?: ButtonVariant;
  /**
   * When true, renders the button with fully rounded corners (pill shape).
   * Can be combined with any variant.
   */
  pillShape?: boolean;
  /**
   * When true, adds a ChevronDown icon as endContent to indicate a dropdown trigger.
   * Can be combined with any variant.
   */
  dropdownChevron?: boolean;
  /**
   * Button color theme
   * - "primary" (default): Brand blue
   * - "danger": Red color (typically used with variant="secondary" for secondary danger)
   */
  color?: ButtonColor;
  /**
   * Route path when used as a React Router link. When set (without onPress), the
   * button renders as a styled `<Link>` instead of a button.
   */
  to?: string;
  /**
   * Plain anchor target. Renders the button as a styled `<a href>` with no
   * router dependency — use for hard navigation (e.g. error views that may
   * render outside the router).
   */
  href?: string;
  /**
   * When true, shows a loading spinner in place of the leading icon and disables
   * interaction.
   */
  isLoading?: boolean;
  /**
   * Extra classes merged onto the button.
   */
  className?: string;
  /**
   * Button content.
   */
  children?: ReactNode;
  /**
   * When true, indicates the button represents the currently active option (e.g. in a view switcher).
   * Only changes visual appearance for tertiary variant, which is typically used for mode-switching buttons.
   */
  isActive?: boolean;
}

export interface ButtonProps extends ButtonBaseProps {
  /**
   * Carbon icon component to display before the button text. This overrides the startContent prop.
   */
  icon?: CarbonIconType;
  /**
   * Button label (text shown on the button). Use this for config-style APIs; equivalent to children.
   * When both are provided, label takes precedence.
   */
  label?: ReactNode;
}

/**
 * Shared Button component based on Design System and HeroUI
 *
 * For icon-only buttons (no text or label), use IconButton instead.
 */
export default function Button(props: ButtonProps) {
  if (isLinkButton(props)) {
    return <LinkButton {...props} />;
  }

  const resolvedProps = resolveButtonProps(props);

  return <HeroButton {...resolvedProps} />;
}
